/**
 * User Settings Service
 * Manages user preferences and settings with Firebase Firestore
 */

import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';

// Default user settings structure
const DEFAULT_SETTINGS = {
  // Display preferences
  theme: 'dark', // 'light', 'dark', 'auto'
  compactMode: false,
  showDecimals: true,
  
  // Odds preferences
  oddsFormat: 'american', // 'american', 'decimal', 'fractional'
  defaultSport: 'all',
  showLiveOddsOnly: false,
  autoRefreshOdds: true,
  refreshInterval: 30, // seconds
  
  // Notification preferences
  pushNotifications: false,
  emailNotifications: false,
  favoriteTeamAlerts: false,
  promoAlerts: true,
  
  // Sportsbook preferences (will integrate with existing sportsbookPreferences.js)
  sportsbookPreference: [],
  hiddenSportsbooks: [],
  
  // Chat preferences
  chatEnabled: true,
  showUserAvatars: true,
  chatNotifications: true,
  
  // Privacy preferences
  publicProfile: false,
  shareWinnings: false,
  analyticsOptIn: true,
  
  // App behavior
  autoLogin: false,
  biometricAuth: false,
  sessionTimeout: 30, // minutes
  
  // Betting preferences
  favoriteTeams: [],
  defaultBetAmount: 25,
  maxBetWarning: 500,
  
  // Advanced settings
  developerMode: false,
  betaFeatures: false,
  
  // Metadata
  createdAt: null,
  updatedAt: null,
  version: '1.0.0'
};

export class UserSettingsService {
  static listeners = new Set();

  /**
   * Get user settings from Firestore with fallback to defaults
   * @param {string} userId - Optional user ID, uses current user if not provided
   * @returns {Promise<Object>} User settings object
   */
  static async getUserSettings(userId = null) {
    try {
      const user = userId || auth.currentUser?.uid;
      if (!user) {
        // Return defaults for unauthenticated users with localStorage fallback
        return this.getLocalSettings();
      }

      const userSettingsRef = doc(db, 'userSettings', user);
      const settingsDoc = await getDoc(userSettingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        // Merge with defaults to ensure all properties exist (for version compatibility)
        return { ...DEFAULT_SETTINGS, ...settings };
      } else {
        // Create default settings for new user
        const newSettings = {
          ...DEFAULT_SETTINGS,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await this.saveUserSettings(newSettings, user);
        return newSettings;
      }
    } catch (error) {
      console.error('Error getting user settings:', error);
      // Fallback to local storage or defaults
      return this.getLocalSettings();
    }
  }

  /**
   * Save user settings to Firestore and localStorage
   * @param {Object} settings - Settings object to save
   * @param {string} userId - Optional user ID, uses current user if not provided
   * @returns {Promise<boolean>} Success status
   */
  static async saveUserSettings(settings, userId = null) {
    try {
      const user = userId || auth.currentUser?.uid;
      const updatedSettings = {
        ...settings,
        updatedAt: new Date().toISOString()
      };

      // Always save to localStorage for immediate access
      this.saveLocalSettings(updatedSettings);

      if (!user) {
        console.warn('No authenticated user, settings saved locally only');
        return true;
      }

      const userSettingsRef = doc(db, 'userSettings', user);
      
      // Check if document exists
      const settingsDoc = await getDoc(userSettingsRef);
      
      if (settingsDoc.exists()) {
        await updateDoc(userSettingsRef, updatedSettings);
      } else {
        await setDoc(userSettingsRef, {
          ...updatedSettings,
          createdAt: updatedSettings.createdAt || new Date().toISOString()
        });
      }

      // Notify listeners of settings change
      this.notifyListeners(updatedSettings);
      
      return true;
    } catch (error) {
      console.error('Error saving user settings:', error);
      // Still save locally if Firestore fails
      this.saveLocalSettings(settings);
      return false;
    }
  }

  /**
   * Update specific setting(s)
   * @param {Object} updates - Object with setting key-value pairs to update
   * @param {string} userId - Optional user ID
   * @returns {Promise<boolean>} Success status
   */
  static async updateSettings(updates, userId = null) {
    try {
      const currentSettings = await this.getUserSettings(userId);
      const updatedSettings = { ...currentSettings, ...updates };
      return await this.saveUserSettings(updatedSettings, userId);
    } catch (error) {
      console.error('Error updating settings:', error);
      return false;
    }
  }

  /**
   * Get a specific setting value
   * @param {string} key - Setting key (supports dot notation for nested objects)
   * @param {any} defaultValue - Default value if setting not found
   * @param {string} userId - Optional user ID
   * @returns {Promise<any>} Setting value
   */
  static async getSetting(key, defaultValue = null, userId = null) {
    try {
      const settings = await this.getUserSettings(userId);
      return this.getNestedValue(settings, key) ?? defaultValue;
    } catch (error) {
      console.error('Error getting setting:', error);
      return defaultValue;
    }
  }

  /**
   * Set a specific setting value
   * @param {string} key - Setting key (supports dot notation for nested objects)
   * @param {any} value - Value to set
   * @param {string} userId - Optional user ID
   * @returns {Promise<boolean>} Success status
   */
  static async setSetting(key, value, userId = null) {
    try {
      const updates = this.setNestedValue({}, key, value);
      return await this.updateSettings(updates, userId);
    } catch (error) {
      console.error('Error setting value:', error);
      return false;
    }
  }

  /**
   * Listen to real-time settings changes
   * @param {Function} callback - Callback function to handle settings changes
   * @param {string} userId - Optional user ID
   * @returns {Function} Unsubscribe function
   */
  static subscribeToSettings(callback, userId = null) {
    const user = userId || auth.currentUser?.uid;
    if (!user) {
      console.warn('Cannot subscribe to settings: no authenticated user');
      return () => {};
    }

    const userSettingsRef = doc(db, 'userSettings', user);
    
    const unsubscribe = onSnapshot(userSettingsRef, (doc) => {
      if (doc.exists()) {
        const settings = { ...DEFAULT_SETTINGS, ...doc.data() };
        this.saveLocalSettings(settings); // Keep local cache in sync
        callback(settings);
      }
    }, (error) => {
      console.error('Error in settings subscription:', error);
    });

    this.listeners.add(unsubscribe);
    return unsubscribe;
  }

  /**
   * Reset settings to defaults
   * @param {string} userId - Optional user ID
   * @returns {Promise<boolean>} Success status
   */
  static async resetToDefaults(userId = null) {
    try {
      const defaultSettings = {
        ...DEFAULT_SETTINGS,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return await this.saveUserSettings(defaultSettings, userId);
    } catch (error) {
      console.error('Error resetting settings:', error);
      return false;
    }
  }

  /**
   * Get settings from localStorage
   * @returns {Object} Settings from localStorage or defaults
   */
  static getLocalSettings() {
    try {
      const saved = localStorage.getItem('bettorsadvantage_user_settings');
      if (saved) {
        const settings = JSON.parse(saved);
        return { ...DEFAULT_SETTINGS, ...settings };
      }
      return { ...DEFAULT_SETTINGS };
    } catch (error) {
      console.error('Error loading settings from localStorage:', error);
      return { ...DEFAULT_SETTINGS };
    }
  }

  /**
   * Save settings to localStorage
   * @param {Object} settings - Settings object to save
   */
  static saveLocalSettings(settings) {
    try {
      localStorage.setItem('bettorsadvantage_user_settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings to localStorage:', error);
    }
  }

  /**
   * Migrate settings from old storage keys (like sportsbook preferences)
   * @param {string} userId - User ID to migrate settings for
   */
  static async migrateExistingSettings(userId) {
    try {
      // Migrate sportsbook preferences if they exist
      const sportsbookOrder = localStorage.getItem('bettorsadvantage_sportsbook_order');
      if (sportsbookOrder) {
        const parsed = JSON.parse(sportsbookOrder);
        await this.setSetting('sportsbookPreference', parsed, userId);
      }

      // Add other migrations as needed
      console.log('Settings migration completed for user:', userId);
    } catch (error) {
      console.error('Error during settings migration:', error);
    }
  }

  // Helper methods for nested object access
  static getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  static setNestedValue(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!(key in current)) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
    return obj;
  }

  // Notify all listeners of settings changes
  static notifyListeners(settings) {
    window.dispatchEvent(new CustomEvent('userSettingsChanged', {
      detail: { settings }
    }));
  }

  // Clean up all listeners
  static cleanup() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
  }
}

export default UserSettingsService;